import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/team/login")({
  head: () => ({
    meta: [
      { title: "Team Login — Quibblies" },
      {
        name: "description",
        content: "Staff sign-in for Quibblies moderators and administrators.",
      },
      { property: "og:title", content: "Team Login — Quibblies" },
      { property: "og:description", content: "Staff sign-in for the Quibblies team." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TeamLoginPage,
});

function TeamLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [unverified, setUnverified] = React.useState<string | null>(null);


  /**
   * Staff sign-in only lands in the team area if the account really is staff
   * AND the email address behind it has been verified — an unverified address
   * is not proof of who is holding the account.
   */
  async function completeStaffCheck(userId: string) {
    const { data: staff } = await supabase.rpc("is_staff", { _user_id: userId });
    const { data: current } = await supabase.auth.getUser();
    setBusy(false);

    if (!staff) {
      await supabase.auth.signOut();
      toast.error("Not a team account", {
        description: "This entrance is for Quibblies moderators and administrators.",
      });
      return;
    }

    if (!current.user?.email_confirmed_at) {
      const address = current.user?.email ?? email;
      await supabase.auth.signOut();
      setUnverified(address);
      toast.error("Verify your email first", {
        description: "Team accounts must have a confirmed email address before signing in.",
      });
      return;
    }

    toast.success("Welcome back");
    void navigate({ to: "/moderation", replace: true });
  }

  /** Sends a fresh confirmation email to a staff account that has not verified. */
  async function resendVerification() {
    if (!unverified) return;
    setBusy(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: unverified,
      options: { emailRedirectTo: `${window.location.origin}/team/login` },
    });
    setBusy(false);
    if (error) {
      toast.error("Could not resend", { description: error.message });
      return;
    }
    toast.success("Verification email sent", { description: `Check ${unverified}.` });
  }



  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.user) {
      setBusy(false);
      toast.error("Sign in failed", { description: error?.message ?? "Check your details." });
      return;
    }

    await completeStaffCheck(data.user.id);
  }

  async function handleGoogle() {
    setBusy(true);
    const result = (await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    })) as { error?: { message?: string } | Error; redirected?: boolean };
    if (result.error) {
      setBusy(false);
      toast.error("Google sign-in failed", { description: result.error.message });
      return;
    }
    if (result.redirected) return;

    const { data } = await supabase.auth.getUser();
    if (!data.user) {
      setBusy(false);
      toast.error("Google sign-in failed");
      return;
    }
    await completeStaffCheck(data.user.id);
  }


  return (
    <div className="relative flex min-h-[calc(100vh-8rem)] items-center justify-center px-4 py-16">
      <div className="absolute inset-0 bg-hero-glow" aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-[1.75rem] border border-border/70 bg-gradient-card p-8 shadow-lift">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
          <Lock className="h-4 w-4" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-display text-2xl text-foreground">Team login</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Restricted entrance for Quibblies moderators and administrators. Member accounts should
          sign in through the normal page.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-email">Work email</Label>
            <Input
              id="team-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="team-password">Password</Label>
            <Input
              id="team-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <Button type="submit" variant="hero" className="w-full" size="pill" disabled={busy}>
            {busy ? "Checking…" : "Sign in to the team area"}
          </Button>
        </form>

        <Button
          type="button"
          variant="outline"
          className="mt-3 w-full"
          size="pill"
          onClick={handleGoogle}
          disabled={busy}
        >
          Continue with Google
        </Button>

        {unverified ? (
          <div className="mt-5 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
            <p className="font-medium text-amber-200">Confirm your email to unlock team access</p>
            <p className="mt-1 text-muted-foreground">
              We sent a confirmation link to <span className="font-medium">{unverified}</span>. Open
              it (check spam and promotions), then sign in here again. Team accounts stay locked
              until the address is verified.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={resendVerification}
              disabled={busy}
            >
              Resend the verification email
            </Button>
          </div>
        ) : null}


        <p className="mt-6 text-xs text-muted-foreground">
          Not staff?{" "}
          <Link to="/auth" className="text-primary-glow underline-offset-4 hover:underline">
            Member sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
